/**
 * Drain a blob-URL cache, revoking its URLs once the current render has settled.
 *
 * The card components cache `URL.createObjectURL` results by card id so the
 * mockup gets a stable URL across notify() calls. Entries are normally revoked
 * per-card in remove/clear handlers. A re-seed replaces the whole card array at
 * once, so any entries left from the previous seed become unreachable — nothing
 * holds the id any more (ids come from a monotonic counter) and the URLs leak.
 *
 * Revoking must NOT happen synchronously inside the seeding effect. At that
 * point the parent still holds the old URLs in its mockup state from the last
 * onItemsChange; the replacement URLs only reach it after the re-seed render
 * runs notify(). Revoking inline opens a window where the parent renders an
 * <img> pointing at a dead URL — an image still decoding at that instant breaks,
 * which is the blank-thumbnail symptom the re-seed work exists to fix.
 *
 * Deferring past the current task lets the commit + notify effects flush first,
 * so by the time the URLs die nothing references them.
 *
 * The key type is irrelevant — only the values are read — so callers are free to
 * key by card id, or by `${cardId}:${ratio}` when a card has several crops.
 */
export function revokeCachedBlobUrls(cache: Map<number | string, string>): void {
  if (cache.size === 0) return;
  const stale = Array.from(cache.values());
  cache.clear();
  setTimeout(() => {
    stale.forEach((url) => URL.revokeObjectURL(url));
  }, 0);
}
